import React from "react";

interface ErrorBoundaryProps {
    children: React.ReactNode;
}

interface ErrorBoundaryState {
    showErrors: boolean;
    hasError: boolean;
    error: Error | null;
    info: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { showErrors: false, hasError: false, error: null, info: null };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        // Display fallback UI
        this.setState({ hasError: true, error, info });
    }

    setShowErrors(showErrors: boolean) {
        this.setState({ showErrors });
    }

    render() {
        if (this.state.hasError) {
            // You can render any custom fallback UI
            return (
                <div className="flex flex-col items-center gap-4 p-4">
                    <div>
                        <p className="text-text">An error occurred, reload the page or go back and try again.</p>
                    </div>
                    <div className="flex flex-row gap-2">
                        <button
                            className="px-4 py-2 bg-background-front text-text rounded-md hover:bg-background-back border border-border transition-colors"
                            onClick={() => (window.location.pathname = "/")}
                        >
                            Back
                        </button>
                        <button
                            className="px-4 py-2 bg-brand text-white rounded-md hover:bg-brand-dark transition-colors"
                            onClick={() => window.location.reload()}
                        >
                            Reload
                        </button>
                    </div>
                    <p
                        className="text-sm text-text-muted cursor-pointer hover:text-text"
                        onClick={() => this.setShowErrors(!this.state.showErrors)}
                    >
                        Details
                    </p>
                    {this.state.showErrors && (
                        <>
                            <br />
                            <code className="text-sm">{JSON.stringify(this.state.error, null, 2)}</code>
                            <br />
                            <pre className="text-sm text-text-weak">{this.state.info?.componentStack}</pre>
                        </>
                    )}
                </div>
            );
        }
        return this.props.children;
    }
}

export default ErrorBoundary;
